import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAcceptContract, useActiveContract } from '../../hooks/useUserQueries';
import { ROUTES, STORAGE_KEYS } from '../../utils/constants';
import { getRedirectPath } from '../../utils/roleUtils';
import Header from '../layout/Header';
import Button from '../common/Button';
import './ContractGate.css';

const ContractGate = () => {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const acceptContractMutation = useAcceptContract();
  const {
    data: activeContract,
    isLoading: isLoadingContract,
    isError: isContractError,
    error: contractError,
  } = useActiveContract();

  const [fullName, setFullName] = useState(
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
  );
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (activeContract?.contract_key && activeContract?.version) {
      localStorage.setItem(
        STORAGE_KEYS.ACTIVE_CONTRACT,
        JSON.stringify({
          contract_key: activeContract.contract_key,
          title: activeContract.title,
          version: activeContract.version,
          pdf_url: activeContract.pdf_url,
        })
      );
    }
  }, [activeContract]);

  const contractErrorMessage = useMemo(() => {
    if (!isContractError) return '';
    const detailMessage = contractError?.data?.detail;
    if (contractError?.status === 404) {
      return detailMessage || 'No active contract for your role.';
    }
    if (contractError?.status === 409) {
      return detailMessage || 'Your role is not configured for contracts.';
    }
    return detailMessage || 'Failed to load the contract. Please try again.';
  }, [contractError, isContractError]);

  useEffect(() => {
    if (!isContractError) return;
    const status = contractError?.status;
    if (status !== 404 && status !== 409) return;

    localStorage.setItem(
      STORAGE_KEYS.ACTIVE_CONTRACT,
      JSON.stringify({ missing: true })
    );

    if (location.state?.returnToDocuments) {
      navigate(`${ROUTES.SIGNUP}?step=documents`, { replace: true });
      return;
    }

    const redirectPath = getRedirectPath(user);
    navigate(redirectPath, { replace: true, state: { from: location } });
  }, [contractError, isContractError, location, navigate, user]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!activeContract?.contract_key || !activeContract?.version) {
      setError('Active contract details are missing. Please try again.');
      return;
    }

    if (!accepted) {
      setError('Please confirm that you accept the contract.');
      return;
    }

    if (!fullName.trim()) {
      setError('Please enter your full legal name.');
      return;
    }

    const acceptedAt = new Date().toISOString();

    try {
      const updatedUser = await acceptContractMutation.mutateAsync({
        contract_key: activeContract.contract_key,
        contract_version: activeContract.version,
        contract_signed_name: fullName.trim(),
        contract_accepted_at: acceptedAt
      });

      if (updatedUser) {
        updateUser(updatedUser);
      }

      localStorage.setItem(
        STORAGE_KEYS.CONTRACT_ACCEPTANCE,
        JSON.stringify({
          contractKey: activeContract.contract_key,
          version: activeContract.version,
          acceptedAt,
          signedName: fullName.trim()
        })
      );

      if (location.state?.returnToDocuments) {
        navigate(`${ROUTES.SIGNUP}?step=documents`, { replace: true });
        return;
      }

      const redirectPath = getRedirectPath(updatedUser || user);
      navigate(redirectPath, { replace: true, state: { from: location } });
    } catch (submitError) {
      if (submitError?.status === 404) {
        setError('No active contract for your role. Please contact support.');
      } else if (submitError?.status === 409) {
        setError('Contract version mismatch. Please refresh and try again.');
      } else {
        setError('Failed to record your acceptance. Please try again.');
      }
    }
  };

  return (
    <div className="contract-gate">
      <Header />
      <div className="contract-gate__content">
        <div className="contract-gate__container">
          <h1 className="contract-gate__title">Contract Agreement</h1>
          <p className="contract-gate__subtitle">
            Please review and accept the contract before continuing.
          </p>

          <div className="contract-gate__document">
            <div className="contract-gate__document-header">
              <span>
                {activeContract?.title || 'Contract'}{' '}
                {activeContract?.version ? `(v${activeContract.version})` : ''}
              </span>
              {activeContract?.pdf_url && (
                <a
                  href={activeContract.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="contract-gate__document-link"
                >
                  Open in new tab
                </a>
              )}
            </div>
            {isLoadingContract ? (
              <div className="contract-gate__loading">Loading contract...</div>
            ) : contractErrorMessage ? (
              <div className="contract-gate__loading">Continuing...</div>
            ) : activeContract?.pdf_url ? (
              <iframe
                title={activeContract?.title || 'Contract'}
                className="contract-gate__iframe"
                src={activeContract.pdf_url}
              />
            ) : (
              <div className="contract-gate__error">
                Contract PDF is unavailable. Please contact support.
              </div>
            )}
          </div>

          <form className="contract-gate__form" onSubmit={handleSubmit}>
            {error && <div className="contract-gate__error">{error}</div>}

            <label className="contract-gate__label" htmlFor="contractFullName">
              Full legal name
            </label>
            <input
              id="contractFullName"
              type="text"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              placeholder="Enter your full name"
              className="contract-gate__input"
              disabled={acceptContractMutation.isPending}
              autoComplete="name"
            />

            <label className="contract-gate__checkbox">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
                disabled={acceptContractMutation.isPending || isLoadingContract || !!contractErrorMessage}
              />
              I have read and agree to the contract terms.
            </label>

            <Button
              type="submit"
              variant="primary"
              size="large"
              disabled={
                acceptContractMutation.isPending ||
                isLoadingContract ||
                !!contractErrorMessage ||
                !activeContract?.contract_key ||
                !activeContract?.version
              }
              className="contract-gate__submit"
            >
              {acceptContractMutation.isPending ? 'Saving...' : 'Accept Contract'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContractGate;

